'use client';

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import CmsImage from '@/components/ui/CmsImage';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { useModalScrollLock } from '@/hooks/useModalScrollLock';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getStrapiMediaUrl } from '@/lib/strapi-media-url';
import type { PncModalContent } from '@/types/people-and-culture';

interface WonderShaktiModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: PncModalContent | null;
  locationText?: string | null;
}

export default function WonderShaktiModal({
  isOpen,
  onClose,
  content,
  locationText,
}: WonderShaktiModalProps) {
  const [mounted, setMounted] = useState(false);
  const handleClose = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useModalScrollLock(isOpen, handleClose);

  if (!mounted || !isOpen || !content) return null;

  const heroImage = getStrapiMediaUrl(content.photoGallery?.[0]?.media, 'large');
  const galleryImages = (content.photoGallery ?? [])
    .map((item) => getStrapiMediaUrl(item.media, 'medium'))
    .filter((url): url is string => Boolean(url));
  const closeButton = content.closeButton?.[0];

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={handleClose}
      onWheel={(e) => e.preventDefault()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="wonder-shakti-modal-title"
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 bg-white px-5 py-3.5">
          <div className="flex items-center gap-3">
            {content.headingwithPill ? (
              <span className="rounded-full bg-[#ed1c22] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
                {content.headingwithPill}
              </span>
            ) : null}
            <span id="wonder-shakti-modal-title" className="text-sm font-semibold text-[#0a0a0a]">
              {content.topTitle}
            </span>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition hover:bg-gray-200 hover:text-[#0a0a0a] text-xs"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {heroImage ? (
            <div className="relative h-[240px] w-full shrink-0 sm:h-[320px] md:h-[380px]">
              <CmsImage
                src={heroImage}
                alt={content.topTitle ?? 'Campaign story'}
                fill
                sizes="(max-width: 768px) 100vw, 768px"
                className="object-cover"
                fallbackSrc={MEDIA_PLACEHOLDER}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
              {content.modalTitle ? (
                <p className="absolute bottom-4 left-4 right-4 text-base font-bold leading-snug text-white sm:bottom-5 sm:left-5 sm:right-5 sm:text-lg md:text-xl">
                  {content.modalTitle}
                </p>
              ) : null}
            </div>
          ) : null}

          {content.content ? (
            <div
              className="px-5 py-6 text-sm leading-relaxed text-gray-600 sm:px-7 sm:py-8 md:px-10 [&_blockquote]:my-6 [&_blockquote]:rounded-r-xl [&_blockquote]:border-l-4 [&_blockquote]:border-[#ed1c22] [&_blockquote]:bg-gray-50 [&_blockquote]:px-6 [&_blockquote]:py-5 [&_blockquote_p]:text-base [&_blockquote_p]:font-medium [&_blockquote_p]:italic [&_blockquote_p]:leading-relaxed [&_blockquote_p]:text-[#0a0a0a] [&_figure]:my-6 [&_figure]:overflow-hidden [&_figure]:rounded-xl [&_img]:h-auto [&_img]:w-full [&_p]:mb-5"
              dangerouslySetInnerHTML={{ __html: content.content }}
            />
          ) : null}

          {galleryImages.length > 0 ? (
            <div className="px-5 pb-8 sm:px-7 md:px-10">
              <p className="mb-4 text-[0.62rem] font-bold uppercase tracking-[0.25em] text-gray-400">
                Photo Gallery
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {galleryImages.map((src) => (
                  <div
                    key={src}
                    className="relative h-24 overflow-hidden rounded-lg sm:h-28 md:h-36"
                  >
                    <CmsImage
                      src={src}
                      alt=""
                      fill
                      sizes="(max-width: 640px) 50vw, 33vw"
                      className="object-cover transition-transform duration-300 hover:scale-105"
                      fallbackSrc={MEDIA_PLACEHOLDER}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-col gap-3 border-t border-gray-100 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7">
          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400 sm:gap-6">
            {content.yearOfCampaign ? (
              <span className="font-semibold text-[#0a0a0a]">{content.yearOfCampaign}</span>
            ) : null}
            {locationText ? (
              <span className="flex items-center gap-1.5">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  className="h-3.5 w-3.5"
                >
                  <circle cx="12" cy="10" r="3" />
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                </svg>
                {locationText}
              </span>
            ) : null}
          </div>
          {closeButton?.buttonText?.trim() ? (
            <SwapTextButton
              text={closeButton.buttonText.trim()}
              showArrow={false}
              hoverText={closeButton.buttonText.trim()}
              aria-label={closeButton.ariaLabel?.trim() || closeButton.buttonText.trim()}
              onClick={handleClose}
              className="w-full justify-center !rounded-full !px-5 !py-2 !text-xs sm:w-auto"
            />
          ) : (
            <button
              type="button"
              onClick={handleClose}
              className="w-full rounded-full bg-[#ed1c22] px-5 py-2 text-xs font-semibold text-white sm:w-auto"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
